import { NEUCLEOS_AUTO_SLUG, type ModelCatalog, type ProviderInstanceId, type ServerProviderModel } from "@cleo/contracts";
import { managedModelChoices } from "@cleo/shared/managedModelChoices";
import {
  isProviderInstancePickerReady,
  isProviderInstancePickerVisible,
  type ProviderInstanceEntry,
} from "../../providerInstances";

/** An empty managed section while the World's catalog is still being read. */
export const MANAGED_CATALOG_LOADING = "Reading this World's models…";

export interface ManagedChooserRow {
  readonly instanceId: ProviderInstanceId;
  readonly slug: string;
  readonly label: string;
  readonly cost: string;
  readonly readiness?: string;
}

/** Model-specific controls come from the same World offer as the picker. */
export function managedComposerModels(catalog: ModelCatalog | null): ServerProviderModel[] {
  if (!catalog) return [];
  const modelRow = (
    slug: string,
    name: string,
    model: ModelCatalog["models"][number],
    currentValue: string | undefined,
  ): ServerProviderModel => ({
    slug,
    name,
    isCustom: false,
    capabilities: {
      optionDescriptors:
        (model.reasoningLevels?.length ?? 0) > 1
          ? [
              {
                id: "reasoningLevel",
                label: "Thinking",
                type: "select",
                ...(currentValue ? { currentValue } : {}),
                options: model.reasoningLevels!.map((level) => ({
                  id: level,
                  label:
                    level === "none"
                      ? "Off"
                      : level === "xhigh"
                        ? "Extra high"
                        : level[0]!.toUpperCase() + level.slice(1),
                  isDefault: level === currentValue,
                })),
              },
            ]
          : [],
    },
  });
  const models = catalog.models.filter((model) => model.usable && model.sold);
  return [
    ...models.map((model) =>
      modelRow(`${model.provider}/${model.model}`, model.model, model, model.reasoningDefault),
    ),
    ...catalog.tiers.flatMap((tier) => {
      const model = models.find(
        (model) => model.provider === tier.provider && model.model === tier.model,
      );
      return model
        ? [
            modelRow(
              `neucleos/${tier.tier}`,
              { fast: "Fast", standard: "Standard", deep: "Deep" }[tier.tier],
              model,
              tier.reasoningDefault,
            ),
          ]
        : [];
    }),
    // Neucleos Auto takes no Thinking control: the World sends each message on its route as it stands.
    ...(catalog.auto ? [{ slug: NEUCLEOS_AUTO_SLUG, name: "Auto", isCustom: false, capabilities: { optionDescriptors: [] } }] : []),
  ];
}

/** Intersect the authority offer with the environment's executable inventory. */
export function resolveModelChooserRoot(
  instances: ReadonlyArray<ProviderInstanceEntry>,
  catalog: ModelCatalog | null,
) {
  const platform = instances.find(
    (entry) =>
      entry.instanceId === "neucleos" &&
      entry.driverKind === "aiSdk" &&
      isProviderInstancePickerReady(entry),
  );
  const executable = (slug: string) =>
    platform?.models.some((model) => model.slug === slug) === true ||
    (platform?.snapshot.modelCatalogSource === "world-managed" &&
      /^(anthropic|openai|meta|xai)\/.+/.test(slug));
  const neucleos: ManagedChooserRow[] = [];
  const frontier: ManagedChooserRow[] = [];
  if (platform && catalog) {
    // The World's offer, named and priced by the rule Pocket and the World share (@cleo/shared/managedModelChoices).
    for (const choice of managedModelChoices(catalog)) {
      if (!executable(choice.slug)) continue;
      (choice.section === "neucleos" ? neucleos : frontier).push({
        instanceId: platform.instanceId,
        slug: choice.slug,
        label: choice.label,
        cost: choice.cost,
        ...(choice.readiness ? { readiness: choice.readiness } : {}),
      });
    }
    // Neucleos Auto (Can 68): offered when the World has a route in effect; each message is charged at the model that
    // answered it, so its line names no single price. Why it cannot run just now is the World's own words.
    if (catalog.auto && executable(NEUCLEOS_AUTO_SLUG))
      neucleos.push({
        instanceId: platform.instanceId,
        slug: NEUCLEOS_AUTO_SLUG,
        label: "Auto",
        cost: `Each message at the price of the model that answers: ${catalog.auto.models.map((one) => one.model).join(", ")}`,
        ...(catalog.auto.canRun ? {} : { readiness: catalog.auto.reason ?? "Neucleos Auto cannot run now" }),
      });
  }
  const localFast = instances.find(
    (entry) => entry.instanceId === "neucleos-local" && isProviderInstancePickerReady(entry),
  );
  for (const model of localFast?.models ?? []) {
    if (model.slug.startsWith("local-fast/"))
      neucleos.push({
        instanceId: localFast!.instanceId,
        slug: model.slug,
        label: "Fast · on this Mac",
        cost: "On this Mac",
      });
  }
  // The person's own local models: Settings → Local models → "Add available
  // models to chat picker" registers them as an AI SDK instance ("Cleo Local")
  // whose `local` family runs on this device. They are the person's, not the
  // World's offer, so they stand in their own group rather than under
  // neucleos; before, they were reachable only by typing in the search.
  const onThisMac: ManagedChooserRow[] = [];
  for (const entry of instances) {
    if (
      entry.driverKind !== "aiSdk" ||
      entry.instanceId === "neucleos" ||
      entry.instanceId === "neucleos-local" ||
      entry.snapshot.connectionWorldKey ||
      entry.snapshot.modelCatalogSource === "world-managed" ||
      !isProviderInstancePickerVisible(entry)
    )
      continue;
    for (const model of entry.models)
      if (model.slug.startsWith("local/"))
        onThisMac.push({
          instanceId: entry.instanceId,
          slug: model.slug,
          label: model.name,
          cost: entry.displayName,
        });
  }
  return {
    neucleos,
    frontier,
    onThisMac,
    subscriptions: instances.filter((entry) => entry.driverKind !== "aiSdk"),
    reason: !catalog
      ? "Managed models are unavailable in this session."
      : !platform
        ? "Managed execution is unavailable on this environment."
        : "No managed model is available for this World.",
  };
}
